import { labelPembayaran } from '../../../utils/labelPembayaran';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from '@mui/material';
import {
  ReceiptOutlined,
  VisibilityOutlined,
  LocalPrintshopOutlined,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { waktuCabang } from '../../../utils/waktuCabang';
import { apiHost } from '../../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

const TransaksiRetail = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [summary, setSummary] = useState({});

  useEffect(() => {
    fetchTransactions();
  }, [selectedDate]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${getApiBaseUrl()}/rekapharianuser`, {
        params: { tanggal: selectedDate },
        withCredentials: true,
      });
      const data = res.data?.data || {};
      const allTransactions = [
        ...(data.transaksiSuccess || []),
        ...(data.transaksiPending || []),
      ];
      setTransactions(allTransactions);
      setSummary({
        totalSuccess: res.data?.totalPenjualanSuccess || 0,
        totalPending: res.data?.totalPenjualanPending || 0,
        totalCash: res.data?.totalPenjualanCashSuccess || 0,
        totalQris: res.data?.totalPenjualanQrisSuccess || 0,
        totalNonCash: res.data?.totalPenjualanNonCashSuccess || 0,
        totalCashPending: res.data?.totalPenjualanCashPending || 0,
        totalQrisPending: res.data?.totalPenjualanQrisPending || 0,
      });
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refund per item oleh admin: penuh = 'cancel' dengan total 0 ("Refund"), sebagian = tetap lunas ("Refund sebagian").
  // Ungu supaya beda dari pending (oranye), batal (merah), dan lunas (hijau).
  const statusRefund = (tx) => {
    if (!(Number(tx?.totalRefund) > 0)) return null;
    const st = tx.status_pembayaran;
    if (st === 'cancel' && Number(tx.totaljual) === 0) return 'Refund';
    if (st === 'settlement' || st === 'capture') return 'Refund sebagian';
    return null;
  };
  const REFUND_SX = { bgcolor: '#7b1fa2', color: '#fff' };

  const getStatusColor = (status) => {
    if (status === 'selesai' || status === 'settlement') return 'success';
    if (status === 'pending') return 'warning';
    if (status === 'cancel' || status === 'cancelled' || status === 'expire') return 'error';
    return 'default';
  };

  const bs3 = {
    panelDefault: {
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: 'none',
      mb: 2,
      bgcolor: '#fff',
    },
    panelHeading: (color = '#f5f5f5', textColor = '#333') => ({
      bgcolor: color,
      color: textColor,
      px: 2,
      py: 1.2,
      borderBottom: '1px solid #ddd',
      borderRadius: '4px 4px 0 0',
      fontWeight: 700,
      fontSize: '14px',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    }),
    tableStriped: {
      '& .MuiTableRow-root:nth-of-type(odd)': { bgcolor: '#f9f9f9' },
      '& .MuiTableRow-root:hover': { bgcolor: '#f5f5f5' },
      '& .MuiTableCell-root': {
        fontSize: '13px',
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        py: 1,
        px: 1,
        borderBottom: '1px solid #ddd',
      },
      '& .MuiTableCell-head': {
        fontWeight: 700,
        bgcolor: '#f5f5f5',
        borderBottom: '2px solid #ddd',
        fontSize: '12px',
        textTransform: 'uppercase',
        color: '#555',
      },
    },
  };

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 } }}>
      {/* Date Filter & Summary */}
      <Card variant="outlined" sx={{ ...bs3.panelDefault, mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                width: { xs: '100%', sm: '200px' },
                '& .MuiOutlinedInput-root': {
                  fontSize: '13px',
                  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                },
              }}
            />
            <Grid container spacing={1} sx={{ flex: 1, minWidth: '300px' }}>
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#666' }}>
                    Success
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#28a745' }}>
                    Rp {Number(summary.totalSuccess || 0).toLocaleString('id-ID')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#666' }}>
                    Pending
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#ffc107' }}>
                    Rp {Number(summary.totalPending || 0).toLocaleString('id-ID')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#666' }}>
                    Cash
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0069d9' }}>
                    Rp {Number(summary.totalCash || 0).toLocaleString('id-ID')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#666' }}>
                    QRIS
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#6610f2' }}>
                    Rp {Number(summary.totalQris || 0).toLocaleString('id-ID')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#666' }}>
                    Non-Tunai
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#8e44ad' }}>
                    Rp {Number(summary.totalNonCash || 0).toLocaleString('id-ID')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ ...bs3.panelDefault }}>
        <CardHeader
          title="Riwayat Transaksi"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
          subheader={loading ? 'Memuat transaksi...' : `${transactions.length} transaksi`}
          subheaderTypographyProps={{ variant: 'caption' }}
          sx={{ pb: 1 }}
        />

        <CardContent sx={{ pt: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : transactions.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
              <ReceiptOutlined sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
              <Typography>Tidak ada transaksi</Typography>
            </Box>
          ) : (
            <Table size="small" sx={bs3.tableStriped}>
              <TableHead>
                <TableRow>
                  <TableCell>No. Invoice</TableCell>
                  <TableCell>Tanggal</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Bayar</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Aksi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.uuid || tx.id}>
                    <TableCell>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {tx.order_id || tx.order_id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {waktuCabang(tx.createdAt || tx.created_at).toLocaleDateString('id-ID')}
                    </TableCell>
                    <TableCell>
                      Rp {Number(tx.totaljual || 0).toLocaleString('id-ID')}
                    </TableCell>
                    <TableCell>
                      {labelPembayaran(tx).toUpperCase()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusRefund(tx) || tx.status_pembayaran || tx.status || 'pending'}
                        size="small"
                        color={statusRefund(tx) ? 'default' : getStatusColor(tx.status_pembayaran || tx.status)}
                        sx={statusRefund(tx) ? REFUND_SX : undefined}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Detail">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedTransaction(tx);
                            setDetailOpen(true);
                          }}
                        >
                          <VisibilityOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd', fontSize: '15px', fontWeight: 700 }}>
          Detail Transaksi
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          {selectedTransaction && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Invoice: {selectedTransaction.order_id || selectedTransaction.no_invoice || '-'}</Typography>
                <Typography>Tanggal: {waktuCabang(selectedTransaction.createdAt).toLocaleString('id-ID')}</Typography>
                <Typography>Total: Rp {Number(selectedTransaction.totaljual || selectedTransaction.total || 0).toLocaleString('id-ID')}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#666', mt: 1 }}>
                  Status: <Chip label={statusRefund(selectedTransaction) || selectedTransaction.status_pembayaran || 'pending'} size="small" color={statusRefund(selectedTransaction) ? 'default' : getStatusColor(selectedTransaction.status_pembayaran)} sx={statusRefund(selectedTransaction) ? REFUND_SX : undefined} />
                </Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Barang</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Harga</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedTransaction.TransaksiDetails || selectedTransaction.items || []).map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.Barang?.namabarang || item.nama || '-'}</TableCell>
                      <TableCell align="right">{item.jumlahbarang || item.quantity || 0}</TableCell>
                      <TableCell align="right">Rp {Number(item.harga || 0).toLocaleString('id-ID')}</TableCell>
                      <TableCell align="right">Rp {Number((item.harga || 0) * (item.jumlahbarang || item.quantity || 0)).toLocaleString('id-ID')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #ddd', p: 1.5 }}>
          <Button onClick={() => setDetailOpen(false)} sx={{ textTransform: 'none' }}>
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransaksiRetail;